import { AfterViewInit, booleanAttribute, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as ace from 'ace-builds';
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-liquid';
import 'ace-builds/src-noconflict/theme-crimson_editor';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { FieldDataType, TextFieldFormat } from 'src/app/modules/core/store/models/document.model';
import { TemplateDesignerService } from '../../../services/template-designer.service';

@Component({
    selector: 'topo-dataset-field-template',
    templateUrl: './dataset-field-template.component.html',
    styleUrls: ['./dataset-field-template.component.scss'],
    standalone: false
})
export class DatasetFieldTemplateComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('editor') private editor: ElementRef<HTMLElement>;

  @Input() set value(value: string) {
    this._value = value;
    if (!!this.aceEditor) {
      this.aceEditor.session.setValue(value);
    }
  }
  get value(): string {
    return this._value;
  }
  private _value: string;
  @Input() title: string;
  @Input() instructions: string;
  @Input() set multiLine(value: boolean) {
    this._multiLine = value;
    if (!!this.aceEditor) {
      this.initEditor();
    }
  }
  get multiLine(): boolean {
    return this._multiLine;
  }
  @Input() hideDatasetSelection: boolean;
  @Input() allowHtmlFields: boolean = false;
  @Input() required: boolean = false;
  @Output() changed = new EventEmitter<string>();

  _multiLine: boolean = false;
  aceEditor: any;
  selectedDatasetUId: string = null;
  editorVariables: string[] = [];
  editorDataTypes: Array<FieldDataType> = [
    FieldDataType.Text,
    FieldDataType.Color,
    FieldDataType.Email,
    FieldDataType.Phone,
    FieldDataType.Number,
    FieldDataType.DateTime
  ];
  selectedDataset: Dataset;
  datasets: Dataset[];

  constructor(private translateService: TranslateService, private templateDesignerService: TemplateDesignerService) {}

  ngOnInit(): void {
    this.datasets = [
      {
        name: this.translateService.instant('COMMUNICATIONS.SELECT-DATASET'),
        uId: null
      } as Dataset,
      ...(this.templateDesignerService?.datasets ?? [])
    ];
  }

  ngAfterViewInit() {
    ace.config.set('fontSize', '14px');
    ace.config.set('basePath', 'path');

    this.aceEditor = ace.edit(this.editor.nativeElement);
    this.initEditor();

    this.aceEditor.setTheme('ace/theme/crimson_editor');
    this.aceEditor.session.setMode(null);

    this.aceEditor.session.setValue(this.value);

    if (this._multiLine == true) {
      this.aceEditor.getSession().setUseWrapMode(true);
    }

    this.aceEditor.getSession().on('change', () => {
      var val = this.aceEditor.getSession().getValue();
      this.changed.emit(val);
    });
    this.aceEditor.getSession().on('paste', function (e) {
      e.text = e.text.replace(/[\r\n]+/g, ' ');
    });
    this.aceEditor.renderer.screenToTextCoordinates = function (x, y) {
      var pos = this.pixelToScreenCoordinates(x, y);
      return this.session.screenToDocumentPosition(
        Math.min(this.session.getScreenLength() - 1, Math.max(pos.row, 0)),
        Math.max(pos.column, 0)
      );
    };
  }

  private initEditor(): void {
    if (this._multiLine == true) {
      this.aceEditor.setOptions({
        maxLines: 7,
        selectionStyle: 'text',
        useWrapMode: true,
        indentedSoftWrap: false,
        autoScrollEditorIntoView: true,
        showGutter: false,
        showLineNumbers: false,
        highlightActiveLine: false,
        behavioursEnabled: false,
        printMargin: false,
        enableBasicAutocompletion: true,
        enableSnippets: false
      });
    } else {
      this.aceEditor.setOptions({
        maxLines: 1,
        autoScrollEditorIntoView: true,
        showGutter: false,
        showLineNumbers: false,
        highlightActiveLine: false,
        behavioursEnabled: false,
        printMargin: false,
        enableBasicAutocompletion: true,
        enableSnippets: false
      });
      // disable Enter Shift-Enter keys
      this.aceEditor.commands.bindKey('Enter|Shift-Enter', 'null');
    }

    var lang = ace.require('ace/ext/language_tools');
    lang.setCompleters([]);
  }

  ngOnDestroy(): void {}

  datasetChanged(): void {
    this.selectedDataset = this.datasets?.find(d => d.uId === this.selectedDatasetUId);

    var lang = ace.require('ace/ext/language_tools');
    lang.setCompleters([]);

    if (!!!this.selectedDataset) {
      return;
    }

    this.selectedDataset?.schema.fields
      .filter(f => this.editorDataTypes.includes(f.dataType))
      .map(field => {
        if (field.dataType === FieldDataType.Text) {
          if (this.allowHtmlFields === true || field?.settings?.format !== TextFieldFormat.MultiLine) {
            this.editorVariables.push(field.systemName);
          }
        } else {
          this.editorVariables.push(field.systemName);
        }
      });

    const editorVariables = this.editorVariables;

    var variablesCompleter = {
      getCompletions: function (editor, session, pos, prefix, callback) {
        callback(
          null,
          editorVariables.map(function (val) {
            return {
              caption: val,
              value: '{{' + val + '}}'
            };
          })
        );
      }
    };

    lang.addCompleter(variablesCompleter);

    this.aceEditor.focus();
  }
}
